import axios from 'axios';
import { normalize } from 'normalizr';
import { createSlice } from '@reduxjs/toolkit';

import NotificationService from '../services/NotificationService';
import * as DataHelpers from '../helpers/DataHelpers';

import { group, tag, channel } from '../actions/NormalizrSchema';
import { setError, setFormInProgress } from './uiReducer';

import * as AuthActionTypes from '../constants/AuthActionTypes';
import * as InboxActionTypes from '../constants/InboxActionTypes';
import * as UserActionTypes from '../constants/UserActionTypes';

import * as ChatActionTypes from '../constants/ChatActionTypes';
import * as ChannelReducer from './channelReducer';
import * as TagReducer from './tagReducer';

// SLICE
const groupSlice = createSlice({
  name: 'GROUP',
  initialState: {
    loading: true,
    groups: {},
    groupIds: [],
    groupSearchIds: [],
    groupSearchLoading: false,
    deleteGroupPreConditions: {},
  },
  reducers: {
    receiveGroups: receiveGroupsData,
    requestData: state => ({
      ...state,
      loading: true,
    }),
    receiveData: state => ({
      ...state,
      loading: false,
    }),
    receiveGroupView: receiveGroupsData,
    requestSearchData: state => ({
      ...state,
      groupSearchLoading: true,
    }),
    receiveGroupsSearch: (state, action) => ({
      ...receiveGroupsData(state, action),
      groupSearchIds: action.payload.groupIds,
      groupSearchLoading: false,
    }),
    clearGroupSearch: state => ({
      ...state,
      groupSearchIds: [],
    }),
    resetGroupData: state => ({
      ...state,
      groupIds: [],
    }),
    receiveDeleteGroupPreconditions: (state, action) => ({
      ...state,
      deleteGroupPreConditions: action.payload,
    }),
    removeGroup: (state, action) => {
      const groupsClone = { ...state.groups };
      delete groupsClone[action.payload.groupId];

      return {
        ...state,
        groups: groupsClone,
        groupIds: state.groupIds.filter(groupId => groupId !== action.payload.groupId),
        loading: false,
      };
    },
  },
  extraReducers: {
    [AuthActionTypes.setUser]: receiveGroupsData,

    [ChatActionTypes.receiveChatInbox]: receiveGroupsData,
    [ChatActionTypes.receiveChatThreadUserView]: receiveGroupsData,
    [ChatActionTypes.receiveChatThreadGroupView]: receiveGroupsData,
    [ChatActionTypes.receiveWebSocketEvent]: receiveGroupsData,
    [ChatActionTypes.receiveCreateChatEvent]: receiveGroupsData,

    [InboxActionTypes.receiveInboxThreadView]: receiveGroupsData,

    [UserActionTypes.receiveMembersView]: receiveGroupsData,
    [UserActionTypes.receiveUsers]: receiveGroupsData,
    [UserActionTypes.receiveContactListView]: receiveGroupsData,
    [UserActionTypes.receiveUsersSearch]: receiveGroupsData,
    [UserActionTypes.receiveConnectedPartySearch]: receiveGroupsData,
    [UserActionTypes.receiveUser]: receiveGroupsData,
    [UserActionTypes.receiveContactList]: receiveGroupsData,
  },
});

export default groupSlice.reducer;

// ACTIONS
export const {
  receiveGroups,
  requestData,
  receiveData,
  receiveGroupView,
  requestSearchData,
  receiveGroupsSearch,
  clearGroupSearch,
  resetGroupData,
  removeGroup,
  receiveDeleteGroupPreconditions,
} = groupSlice.actions;

// REDUCER HELPERS

function receiveGroupsData(state, action) {
  return {
    ...state,
    groups: DataHelpers.mergeShallow(state.groups, action.payload.groups),
    groupIds: [...new Set([...state.groupIds, ...action.payload.groupIds])],
  };
}

// THUNKS -- ASYNC ACTION CREATORS

export function fetchGroups() {
  return (dispatch) => {
    dispatch(requestData());
    dispatch(resetGroupData());

    return axios.get('/groups')
      .then((response) => {
        const normalized = normalize(response.data, [group]);

        dispatch(receiveGroups(getGroupsPayload(normalized)));
        dispatch(receiveData());
      })
      .catch(err => dispatch(handleError(err)));
  };
}

export function fetchGroupsFormView() {
  return (dispatch) => {
    dispatch(requestData());

    return axios.all([getGroups(), TagReducer.getTags(), ChannelReducer.getChannels()])
      .then(axios.spread((groupsResponse, tagsResponse, channelsResponse) => {
        const normalizedGroup = normalize(groupsResponse.data, [group]);
        const normalizedTags = normalize(tagsResponse.data, [tag]);
        const normalizedChannels = normalize(channelsResponse.data, [channel]);

        dispatch(receiveGroupView(getGroupsFormView(normalizedGroup, normalizedTags, normalizedChannels)));
        dispatch(receiveData());
      }))
      .catch(err => dispatch(handleError(err)));
  };
}

export function updateGroup(payload) {
  return (dispatch) => {
    dispatch(setFormInProgress(true));

    return axios.put(`/groups/${payload.id}`, payload)
      .then((response) => {
        dispatch(setError(null));
        NotificationService('updateGroup', response);
      })
      .catch(err => dispatch(handleErrorToast('updateGroup', err)));
  };
}

export function createGroup(payload) {
  return (dispatch) => {
    dispatch(setFormInProgress(true));

    return axios.post('/groups', payload)
      .then((response) => {
        dispatch(setError(null));
        NotificationService('createGroup', response);

        return response.data; // return response from API for proper browserHistory.push(groups/data.id)
      })
      .catch(err => dispatch(handleErrorToast('createGroup', err)));
  };
}

export function fetchGroupsAndPopulateSearchIds(excludeChatGroups = false) {
  const groupTypeFilter = excludeChatGroups ? '?groupTypeIds=59,61' : '';

  return (dispatch) => {
    dispatch(requestSearchData());

    return axios.get(`/groups${groupTypeFilter}`)
      .then((response) => {
        const normalized = normalize(response.data, [group]);

        dispatch(receiveGroupsSearch(getGroupsPayload(normalized)));
      })
      .catch(err => dispatch(handleError(err)));
  };
}

export function fetchGroupSearch(value, excludeChatGroups = false) {
  const groupTypeFilter = excludeChatGroups ? '&groupTypeIds=59,61' : '';

  return (dispatch) => {
    dispatch(requestSearchData());

    return axios.get(`/groups/search?q=${value}${groupTypeFilter}`)
      .then((response) => {
        const normalized = normalize(response.data, [group]);

        dispatch(receiveGroupsSearch(getGroupsPayload(normalized)));
      })
      .catch(err => dispatch(handleError(err)));
  };
}

export function destroyGroupPreconditions(groupId) {
  return dispatch =>
    axios.delete(`/groups/${groupId}`)
      .then((response) => {
        dispatch(receiveDeleteGroupPreconditions(response.data));
      })
      .catch((err) => {
        console.error(err.response); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
}

export function destroyGroup(groupId) {
  return dispatch =>
    axios.delete(`/groups/${groupId}?isDeleteConfirmed=true`)
      .then((response) => {
        if (response.data && response.data.preconditionsForGroupDelete && response.data.preconditionsForGroupDelete.length > 0) {
          dispatch(receiveDeleteGroupPreconditions(response.data));
          return response.data; // return response from API for proper conditionally browserHistory.push('/settings/organization/groups')
        } else {
          dispatch(removeGroup({ groupId }));
          NotificationService('destroyGroup', response);
          return response.data;
        }
      })
      .catch((err) => {
        console.error(err.response); // eslint-disable-line no-console
        dispatch(setError(err.response || err));
        NotificationService('destroyGroup', err.response);
      });
}

function handleError(err) {
  return (dispatch) => {
    const error = err.response || err;

    // eslint-disable-next-line no-console
    console.error(error);

    dispatch(setError(error));
  };
}

function handleErrorToast(action, err) {
  return (dispatch) => {
    console.error(err.response || err); // eslint-disable-line no-console

    dispatch(setError(err.response || err));
    // throw toast only if a 500, all other api errors are set to inline validation (to avoid duplicate errors rendering)
    if (err.response && err.response.status === 500) {
      NotificationService(action, err.response);
    }
  };
}

// AXIOS HELPERS

export function getGroup(groupId) {
  return axios.get(`/groups/${groupId}`)
    .catch(err => console.error(err)); // eslint-disable-line no-console
}

export function getGroups() {
  return axios.get('/groups')
    .catch(err => console.error(err)); // eslint-disable-line no-console
}

export function getDynamicGroupByUserIds(userIds) {
  /**
   * This reduce method is used to transofrm an array of values to query string format for arrays.
   * [1, 2, 3] --> 'userIds[]=1&userIds[]=2&userIds[]=3'
   * A REST API will not properly process an array type inside a query string, but can parse the query string as shown above when an array as a parameter is necessary.
   * The reduce starts with an empty string and concatenates new values based on the current element. '&' character is only appended if the current item is not the last.
   * */
  const shapedQueryString =
    userIds.reduce((queryString, userId, index, userIdsArray) => queryString.concat(`userIds[]=${userId}${index < userIdsArray.length - 1 ? '&' : ''}`), '');

  return axios.get(`/groups/dynamicGroup?${shapedQueryString}`);
}

// PREPARE CALLBACKS -- PAYLOAD CUSTOMIZERS

export function getGroupsPayload(normalizedGroups) {
  return {
    groups: {
      ...normalizedGroups.entities.groups,
    },
    groupIds: normalizedGroups.result,
    channels: {
      ...normalizedGroups.entities.channels,
    },
    phones: {
      ...normalizedGroups.entities.phones,
    },
    phoneIds: DataHelpers.getObjectKeys(normalizedGroups.entities.phones),
    channelIds: DataHelpers.getObjectKeys(normalizedGroups.entities.channels),
    tags: {
      ...normalizedGroups.entities.tags,
    },
    tagIds: DataHelpers.getObjectKeys(normalizedGroups.entities.tags),
    users: {
      ...normalizedGroups.entities.users,
    },
    userIds: DataHelpers.getObjectKeys(normalizedGroups.entities.users),
  };
}

function getGroupsFormView(normalizedGroups, normalizedTags, normalizedChannels) {
  return {
    ...getGroupsPayload(normalizedGroups),
    ...ChannelReducer.getChannelsPayload(normalizedChannels),
    ...TagReducer.getTagsPayload(normalizedTags),
  };
}
